import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDataUser, setTemporalUsers } from "../libs/redux/slices/authSlice/authSlice";
import { useGetUserTemporalQuery, useGetTurnsTodayByUserQuery } from "../libs/redux/slices/authSlice/authApiSlice";
import { BoxStatus, ShiftBanner, Box } from "codekit";
import { Avisoxxx, useDrilldown } from "../components/Tablerox";
import { setInitialFilter } from "../libs/redux/slices/Graficsx/Criticid/criticidSlice";
import { useGetQuantityCriticalQuery } from "../libs/redux/slices/Graficsx/Criticid/criticidApiSlice";
import { Buttonsx, Galerdas } from "../components/Dashboax";
import { useGetVisiblesQuery } from "../libs/redux/slices/Paramgen/Dashestx/dashestxApiSlice";
import { useGetMipuestoWidgetsQuery } from "../libs/redux/slices/Mipuesto/mipuestoApiSlice";
import Midiaxxx from "../components/Mipuesto/Midiaxxx";

/**
 * Mi Puesto de Trabajo = la pantalla de entrada (Diego 2026-09-16): el Inicio y
 * Mi Puesto eran dos versiones de lo mismo. Aquí queda una sola: la cabecera del
 * puesto, el aviso de operaciones críticas, "Mi día" (qué hacer hoy) y el
 * escritorio (plantilla del cargo fija + tableros adicionales con permiso). La
 * única parametría de tableros por cargo es la plantilla de escritorio
 * (CIMS0120); la lista del Inicio (CIMS0037) se retiró. Los tableros estáticos
 * (CIMS0118) siguen en su pestaña.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-09-16
 */
function DashboardHome() {
  const dispatch = useDispatch();
  const [currentView, setCurrentView] = useState("HOME");
  const { USRIDXXX, USRNOMXX } = useSelector(getDataUser);
  //Los tableros estaticos del cargo (CIMS0118). RTK Query cachea la respuesta, asi que Galerdas
  //reusa esta misma consulta y no se pide dos veces.
  const { data: visibles, isLoading: isLoadingVisibles } = useGetVisiblesQuery();
  const { data: turnsByUser } = useGetTurnsTodayByUserQuery({ USRIDXXX });
  const { data: temporalUsers, isSuccess } = useGetUserTemporalQuery({ USRIDXXX });
  //Solo para la cabecera (nombre del cargo); el escritorio lo pinta Miescrix
  const { data: escritorio } = useGetMipuestoWidgetsQuery();
  const cargoDes = String(escritorio?.data?.CARGODES ?? "");
  const [banner, setBanner] = React.useState(null);
  //El aviso de operaciones críticas se cierra por sesión y por cantidad: si cambia el número, vuelve a salir
  const [avisoCerrado, setAvisoCerrado] = useState(() => window.sessionStorage.getItem("aviso-criticas-cerrado"));
  const { ir, filtroIn } = useDrilldown();
  const [isCritical, setIsCritical] = useState(false)
  const { data: criticalOperations, isSuccess: isSuiccessCritical } = useGetQuantityCriticalQuery(
    {},
    { refetchOnMountOrArgChange: true },
  );
  const [quantityCritical, setQuantityCritical] = useState(0)

  const toDateAt = (dateStr, timeStr) => new Date(`${dateStr}T${timeStr}`);

  const fmtHour = (d) =>
    d.toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit', hour12: true });


  //Hook para el seteo de los usuarios reemplazo
  useEffect(() => {
    if (isSuccess) {
      const { data = [] } = temporalUsers;
      dispatch(setTemporalUsers(data));
    }
  }, [isSuccess, temporalUsers, dispatch]);

  //Hook para validar si se encontraron operaciones criticas
  useEffect(() => {
    if (isSuiccessCritical) {
      const { data = {} } = criticalOperations;
      if (Object.keys(data).length > 0) {
        setIsCritical(true)
        setQuantityCritical(data["ROJO"].total)
      }
    }
  }, [isSuiccessCritical, criticalOperations])

  React.useEffect(() => {
    const compute = () => {
      const dayData = turnsByUser?.data;
      const turns = dayData?.TURNSXXX ?? [];

      if (!Array.isArray(turns) || turns.length === 0) {
        setBanner({ visible: false, variant: 'start', primary: '', secondary: '' });
        return;
      }

      const dateStr = dayData.DATEXXXX;

      const ranges = turns
        .flatMap(t => (t.RANGESXX ?? []).map(r => ({
          start: toDateAt(dateStr, r.STARTXXX),
          end: toDateAt(dateStr, r.ENDXXXXX),
        })))
        .sort((a, b) => a.start - b.start);

      const now = new Date();

      const current = ranges.find(r => now >= r.start && now < r.end);
      if (current) {
        setBanner({
          visible: true,
          variant: 'end',
          primary: `Tu turno termina a las ${fmtHour(current.end)}`,
          secondary: 'Recuerda finalizar tu turno en la opción Cerrar Turno',
        });
        return;
      }

      const next = ranges.find(r => now < r.start);
      if (next) {
        setBanner({
          visible: true,
          variant: 'start',
          primary: `Tu turno inicia a las ${fmtHour(next.start)}`,
          secondary: `Hoy (${dayData.DAYXXXXX}). Recuerda finalizar tu turno en la opción Cerrar Turno`,
        });
        return;
      }

      setBanner({ visible: false, variant: 'start', primary: '', secondary: '' });
    };

    compute();
    const id = setInterval(compute, 5 * 60 * 1000); // 5 minutos
    return () => clearInterval(id);
  }, [turnsByUser]);

  //Se espera a los tableros estaticos para que la barra de pestañas no aparezca a medias
  if (isLoadingVisibles) {
    return <BoxStatus variant="loading" text="Cargando información..." />;
  }

  //La pestaña TABLEROS (estaticos, CIMS0118) solo aparece si el cargo tiene alguno
  const hasTableros = (visibles?.data?.length ?? 0) > 0;
  const currentTab = hasTableros ? currentView : "HOME";

  //Aviso de operaciones críticas (kit): al tablero de criticidad si está en el escritorio, o al tracking
  const handleVerTablero = (color) => {
    dispatch(setInitialFilter(color));
    const nodo = document.getElementById("widget-113");
    if (!nodo) {
      handleAbrirTracking(color);
      return;
    }
    setTimeout(() => nodo.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  };
  const fechaHoy = new Date().toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" });
  const handleAbrirTracking = (color) => ir("Modimpxx", "Trackimp", [filtroIn("MDOSYYYY.REGESTXX", "ACTIVO"), filtroIn("MDSSYYYY.COLORXXX", color)]);
  const handleCerrarAviso = () => {
    window.sessionStorage.setItem("aviso-criticas-cerrado", String(quantityCritical));
    setAvisoCerrado(String(quantityCritical));
  };

  return (
    <>
      {/**Operaciones críticas: aviso del kit en vez del modal que tapaba la pantalla (Diego 2026-09-14) */}
      {isCritical && quantityCritical > 0 && avisoCerrado !== String(quantityCritical) && (
        <Box mb={4}>
          <Avisoxxx
            tonoxxx="ROJO"
            kicker={`Bienvenid@ ${USRNOMXX}`}
            valor={quantityCritical}
            titulo={quantityCritical === 1 ? "operación crítica en tu perfil" : "operaciones críticas en tu perfil"}
            texto="Ya incumplen o están al borde de sus términos. Cada número abre su pantalla."
            acciones={[
              { clave: "tracking", texto: "Abrir en el tracking", primaria: true, onClick: () => handleAbrirTracking("ROJO") },
              { clave: "tablero", texto: "Ver en el tablero", onClick: () => handleVerTablero("ROJO") },
            ]}
            onCerrar={handleCerrarAviso}
          />
        </Box>
      )}
      {/* Cabecera del puesto: mismo lenguaje visual de los Tableros */}
      <div style={{ background: "linear-gradient(90deg, #1f3864 0%, #2e5096 100%)", borderRadius: 12, padding: "18px 28px", color: "#fff", marginBottom: 16 }}>
        <div style={{ fontSize: 24, fontWeight: 700 }}>
          Mi Puesto de Trabajo
          {cargoDes && (
            <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 600, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 12, padding: "3px 12px", verticalAlign: "middle" }}>
              {cargoDes}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>{USRNOMXX ?? ""} · {fechaHoy}</div>
      </div>
      {hasTableros && <Buttonsx currentView={currentTab} handleCurrentView={setCurrentView} />}
      <Box style={{ display: currentTab === "HOME" ? "block" : "none" }}>
        {banner?.visible && (
          <Box spacing={5}>
            <ShiftBanner
              variant={banner.variant}
              srcBgImage="/images/cima-icon.png"
              primaryText={banner.primary}
              secondaryText={banner.secondary}
              alignment="left"
              imageRotation={-10}
              imageX={95}
            />
          </Box>
        )}
        <Midiaxxx />
      </Box>
      {hasTableros && currentTab === "TABLEROS" && <Galerdas />}
    </>
  );
}

export default DashboardHome;
